<script setup>
import { computed, ref } from 'vue'
import { ChevronRight } from '@lucide/vue'
import { Badge } from '@drama/ui'

import CallDetails from './CallDetails.vue'
import { clockTime, duration, fullTime, jobRange, useAuditText, variantOf } from './audit-text.js'
import { callTotals } from './run-observer.js'

const props = defineProps({
  // 详情响应的全部平台调用，跨分支，按创建时间升序。
  calls: { type: Array, required: true },
  // callOwners() 的结果。
  owners: { type: Object, required: true },
})

const { t, tr, enumLabel, errorLabel, tokensLabel, costLabel } = useAuditText()
const rows = computed(() => props.calls.map((call, index) => ({
  key: `${index}:${call.llm_call_id ?? ''}`,
  call,
  stage: call.llm_call_id ? props.owners.stage_by_call.get(call.llm_call_id) ?? null : null,
})))
// 展开的行；调用正文按展开懒渲染，一条请求可能几十 KB。
const expanded = ref(new Set())

function toggle(key) {
  if (expanded.value.has(key)) expanded.value.delete(key)
  else expanded.value.add(key)
}

function sourceLabel(call) {
  if (call.request_id) {
    const seq = props.owners.turn_of_request.get(call.request_id)
    return seq ? tr('turns.turn', { seq }) : tr('calls.sourceRequest')
  }
  if (call.job_id) {
    const job = props.owners.job_by_id.get(call.job_id)
    return job ? `${enumLabel('jobs.kinds', job.kind || 'memory')} ${jobRange(job)}` : tr('calls.sourceJob')
  }
  return tr('calls.sourceOther')
}
</script>

<template>
  <div v-if="rows.length" class="au-table-wrap">
    <table class="au-table" data-testid="observer-calls">
      <thead>
        <tr>
          <th :aria-label="tr('calls.expand')" />
          <th>{{ tr('calls.time') }}</th>
          <th>{{ tr('calls.source') }}</th>
          <th>{{ tr('calls.role') }}</th>
          <th>{{ tr('calls.model') }}</th>
          <th>{{ tr('calls.status') }}</th>
          <th class="is-num">{{ t('novelPlay.comparison.modelFirstText') }}</th>
          <th class="is-num">{{ t('novelPlay.comparison.providerFirstFrame') }}</th>
          <th class="is-num">{{ tr('calls.duration') }}</th>
          <th class="is-num">{{ tr('calls.tokens') }}</th>
          <th class="is-num">{{ tr('calls.cost') }}</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="row in rows" :key="row.key">
          <tr class="au-table__row" :class="{ 'is-open': expanded.has(row.key) }" @click="toggle(row.key)">
            <td>
              <button
                type="button"
                class="au-icon-btn"
                :aria-expanded="expanded.has(row.key) ? 'true' : 'false'"
                :aria-label="tr('calls.expand')"
                @click.stop="toggle(row.key)"
              >
                <ChevronRight :size="14" aria-hidden="true" />
              </button>
            </td>
            <td :title="fullTime(row.call.created_at)">{{ clockTime(row.call.created_at) }}</td>
            <td>{{ sourceLabel(row.call) }}</td>
            <td>{{ row.stage?.task ? `${row.stage.task} · ` : '' }}{{ enumLabel('roles', row.call.role) }}</td>
            <td>{{ row.call.model || '—' }}<span v-if="row.call.provider" class="au-muted"> · {{ row.call.provider }}</span></td>
            <td>
              <span class="au-chips">
                <Badge v-if="row.call.status" size="sm" :variant="variantOf(row.call.status)">{{ enumLabel('calls.statuses', row.call.status) }}</Badge>
                <span v-else class="au-muted" :title="tr('calls.unavailable')">—</span>
                <span v-if="row.stage?.error_code" class="au-error" :title="row.stage.error_code">{{ errorLabel(row.stage.error_code) }}</span>
              </span>
            </td>
            <td class="is-num">{{ duration(row.call.first_token_ms) }}</td>
            <td class="is-num">{{ duration(row.call.ttft_ms) }}</td>
            <td class="is-num">{{ duration(row.call.duration_ms) }}</td>
            <td class="is-num">{{ tokensLabel(row.call) }}</td>
            <td class="is-num">{{ costLabel(callTotals([row.call])) }}</td>
          </tr>
          <tr v-if="expanded.has(row.key)" class="au-table__detail">
            <td colspan="11"><CallDetails :call="row.call" /></td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
  <p v-else class="au-empty">{{ tr('empty') }}</p>
</template>
